import React, { useState, useEffect, useCallback } from "react";
import { Tabs, Tab } from "@mui/material";
import "./../../assets/css/Dashboard/PharmacyDetails.scss";
import "./../../assets/css/PatientConsultation.scss";
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from "react-router-dom";
import useTitanApp from "../../hooks/useTitanApp";
import {
  setConsultationLoadError,
  setCurrentGlobalTabText,
  setCurrentGlobalTabValue,
  setisPatientSearched,
  setPatientInfoTab,
} from "../../dispatchers/appDispatchers";
import { RiWalkLine } from "react-icons/ri";
import PatientConsultationMediaButton from "./components/PatientConsultationMediaButton";
import {
  setConsultationStarted,
  setViewOpen,
  setShowReferralLetterDialog,
} from "../../dispatchers/patientDispatchers";
import PatientProfileTranscript from "./components/PatientProfileTranscript/PatientProfileTranscript";
import PatientTemplateDEMDx from "./components/PatientTemplateDEMDx/PatientTemplateDEMDx";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import {
  setOpenAddTemplate,
  setCurrentShowEditAiTemplateModal,
} from "../../dispatchers/patientDispatchers";
import AddPatientTemplate from "./components/PatientTemplateDEMDx/components/AddPatientTemplate";
import { motion, AnimatePresence } from "framer-motion";
import PatientAddVitalsModal from "./components/PatientAddVitalsModal";
import PatientDeleteVitalsModal from "./components/PatientDeleteVitalsModal";
import PatientQuickActions from "./components/PatientQuickActions/PatientQuickActions";
import TextField from "@mui/material/TextField";
import CustomButton from "../controls/CustomButton";
import {
  generateAiTemplate,
  setAiPatientTemplate,
  setCurrentShowPatientDetailsEditModal,
} from "../../dispatchers/patientDispatchers";
import Skeleton from "@mui/material/Skeleton";
import { MdOutlineClose } from "react-icons/md";
import PatientAddAllergiesModal from "./components/PatientAddAllergiesModal";
import PatientDeleteAllergiesModal from "./components/PatientDeleteAllergiesModal";
import PatientAddConditionRelatedModal from "./components/PatientAddConditionRelatedModal";
import PatientDeleteConditionRelatedModal from "./components/PatientDeleteConditionRelatedModal";
import PatientAddMedicationsModal from "./components/PatientAddMedicationsModal";
import PatientDeleteMedicationsModal from "./components/PatientDeleteMedicationsModal";
import PatientPreviousConsultationsModal from "./components/PatientPreviousConsultationsModal";
import UpdateAiCustomTemplate from "../SettingsOptions/Templates/components/Consultation/UpdateAiCustomTemplate";
import PatientDetailsEditModal from "../PatientDashboard/components/PatientDetailsEditModal";
import { LuPaperclip } from "react-icons/lu";
import { BsFillFileEarmarkCheckFill } from "react-icons/bs";
import { X, Paperclip, Package } from "lucide-react";
import AddPatientPopUp from "../GlobalHeader/components/AddPatientPopUp";
import { PrescriptionImagePreviewModal } from "../PrescriptionApproval/components/PrescriptionImagePreviewModal";
import ErrorConsultationModal from "./components/ErrorConsultationModal";
import { PreConsultImagePreviewModal } from "./components/PreConsultImagePreviewModal.tsx";
import ViewDocumentModal from "../ui/ViewDocumentModal.tsx";
import { useDocumentsData } from "@/components/Documents/documentsData";
import MultipleVaccinationPreviewModal from "@/components/PatientDashboardV2/components/MultipleVaccinationPreviewModal";
import EditTemplateModal from "./components/EditTemplateModal/EditTemplateModal.tsx";
import {
  deleteAiTemplateById,
  getAiTemplateByCompanyId,
} from "../../dispatchers/templateDispatchers.js";
import { ReferralLetterDialog } from "./components/ReferralLetterDialog.tsx";
import { ReferralModal } from "./components/ReferralModal.tsx";

const PatientConsultation = ({ stopTranscription, isDemDXGlobalCheckRef }) => {
  const { appState, patientState, templateState, patientPrescriptionState } =
    useTitanApp();
  const {
    darkMode,
    features,
    routedFromScheduler,
    consultationLoadError,
    companyId,
  } = appState || {};
  const {
    openAddTemplate,
    showAddVitalsModal,
    showDeleteVitalsModal,
    aiTemplateLoader,
    showAddAllergiessModal,
    showDeleteAllergiesModal,
    showAddConditionRelatedModal,
    showDeleteConditionRelatedModal,
    showAddMedicationsModal,
    showDeleteMedicationsModal,
    showPreviousConsultationsModal,
    showEditAiTemplateModal,
    templateForEditModal,
    showPatientDetailsEditModal,
    selectedPatientDetails,
    prescriptionViewerOpen,
    showPreConsultImagePreviewModal,
    viewOpen,
    selectedTemplateName,
    showReferralLetterDialog,
  } = patientState || {};

  const { expandedSection, selectedTemplate, aiTemplateLoadError } =
    templateState || {};
  const { previewPrescription } = patientPrescriptionState || {};
  const [promptName, setPromptName] = useState(""); // for generation prompt
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedFileBase64, setAttachedFileBase64] = useState("");
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [referralModalPayload, setReferralModalPayload] = useState({
    title: "",
    content: "",
    subject: "",
    documentType: "letter",
  });
  const { viewingDoc } = useDocumentsData();
  const { ifMapEnabled, ifPatientDashboardEnabled, ifPatientListEnabled } =
    features || {};
  const { pharmacyId } = useParams() || {
    pharmacyId: "00000000-0000-0000-0000-000000000000",
  };

  useEffect(() => {
    document.body.className = `${darkMode ? "dark-theme" : "light-theme"}`;
  }, []);

  // Check input validation: text is required, and if file is attached, text is also required
  const hasValidInput = () => {
    const hasText = promptName.trim();
    const hasFile = attachedFileBase64;

    if (hasFile) {
      // If file is attached, text is required
      return hasText;
    } else {
      // If no file, just text is required
      return hasText;
    }
  };

  const handleGenerate = useCallback(async () => {
    if (hasValidInput()) {
      const data = {
        userPrompt: promptName.trim(),
        attachment: attachedFileBase64 || "",
      };
      setShowTemplateBuilder(true);
      await generateAiTemplate(data);

      // Clear states after API call
      setPromptName("");
      setAttachedFile(null);
      setAttachedFileBase64("");
    }
  }, [promptName, attachedFileBase64]);

  const handleDialogClose = useCallback(() => {
    setOpenAddTemplate(false);
    setAiPatientTemplate([]);
    setShowTemplateBuilder(false);
  });

  const handleEditAiTemplateDialogClose = useCallback(() => {
    setCurrentShowEditAiTemplateModal(false);
  }, []);

  const handleReferralLetterReady = useCallback(
    (title, content, subject = "", documentType = "letter") => {
      setReferralModalPayload({
        title,
        content,
        subject,
        documentType,
      });
      setReferralModalOpen(true);
    },
    [],
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];

    if (file && allowedTypes.includes(file.type)) {
      setAttachedFile(file);

      const reader = new FileReader();

      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        setAttachedFileBase64(base64String);
      };

      reader.readAsDataURL(file);
    } else {
      setAttachedFile(null);
      setAttachedFileBase64("");
    }
  };

  const handleRemoveFile = () => {
    setAttachedFileBase64("");
    setAttachedFile(null);
  };

  // Determine what to pass to AddPatientTemplate
  const getTemplateData = () => {
    if (attachedFileBase64) {
      return {
        prompt: promptName.trim(),
        attachment: attachedFileBase64,
      };
    } else if (promptName.trim()) {
      return {
        prompt: promptName.trim(),
      };
    }
    return {
      prompt: "",
    };
  };

  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP" && routedFromScheduler) {
      setisPatientSearched(false);
      setPatientInfoTab(null);
      setCurrentGlobalTabValue(2);
      setCurrentGlobalTabText("Calendar");
    } else {
    }
  }, [navigationType, routedFromScheduler]);

  const onDeleteTemplate = useCallback(async (templateId) => {
    if (!templateId) return;

    try {
      await deleteAiTemplateById(templateId);
      await getAiTemplateByCompanyId(companyId);
      // Close edit modal after delete
      setCurrentShowEditAiTemplateModal(false);
    } catch (error) {
      console.error("Error deleting AI template:", error);
    }
  }, []);

  return (
    <>
      <div className="pc-content">
        <div className="pc-content-wrapper">
          <PatientProfileTranscript />
          <PatientTemplateDEMDx
            stopTranscription={stopTranscription}
            isDemDXGlobalCheckRef={isDemDXGlobalCheckRef}
          />
          <PatientQuickActions />
        </div>
      </div>

      <Dialog
        open={openAddTemplate}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenAddTemplate(false);
          }
        }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: !showTemplateBuilder ? "25vw" : "60vw",
            padding: 2, // optional, for spacing
            backgroundColor: darkMode ? "#121212" : "#fff",
            borderRadius: "16px",
          },
        }}
      >
        <div className="consultation_template_heading">
          <div className="create_consult_heading">
            <h5>Create consultation template</h5>
            <p>
              Describe the type of consultation template you want to create{" "}
              {attachedFile ? "(required when file is attached)" : ""}
            </p>
          </div>
          <MdOutlineClose
            fill="#999999"
            onClick={handleDialogClose}
            size={22}
            className="icons-color icons-pointer"
          />
        </div>

        <DialogContent
          className={
            darkMode
              ? "consultation-dialog-content-dark"
              : "consultation-dialog-content"
          }
          sx={{ padding: 0 }}
        >
          {!showTemplateBuilder ? (
            <>
              <div className="add_consultation_template_wrapper">
                <TextField
                  placeholder="e.g., A comprehensive template for diabetes management consultations including blood sugar monitoring, medication review, and lifestyle assessments..."
                  multiline
                  variant="outlined"
                  minRows={4}
                  value={promptName}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      backgroundColor: darkMode ? "#111" : "#fff",
                      fontSize: "14px", // 👈 set font size to 14px
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px", // 👈 ensure input text is 14px
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "14px", // 👈 label font size
                    },
                  }}
                  fullWidth
                  required
                  inputProps={{ maxLength: 500 }}
                  onChange={(e) => {
                    setPromptName(e.target.value);
                  }}
                />
                <p className="char-counter">{promptName.length}/500</p>
              </div>
              <div className="attach-document-wrapper">
                <label htmlFor="file-upload" className="attach-label">
                  <Paperclip className="attach-icon" />
                  <span>Attach document (optional)</span>
                </label>
                <input
                  type="file"
                  id="file-upload"
                  className="hidden-file-input"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.txt"
                />

                {attachedFile && (
                  <div className="attached-file-preview">
                    <Package className="file-icon" />
                    <span className="file-name">{attachedFile.name}</span>
                    <X className="remove-icon" onClick={handleRemoveFile} />
                  </div>
                )}
              </div>
              <div className="consultation_template_button_wrapper">
                <CustomButton
                  isdisable={aiTemplateLoader}
                  action={() => {
                    setOpenAddTemplate(false);
                    setShowTemplateBuilder(false);
                    setPromptName("");
                    setAttachedFile(null);
                    setAttachedFileBase64("");
                  }}
                  label={"Cancel"}
                />
                <CustomButton
                  isdisable={!hasValidInput() || aiTemplateLoader}
                  label={"Generate Template"}
                  action={handleGenerate}
                />
              </div>
            </>
          ) : (
            <AddPatientTemplate
              promptData={getTemplateData().prompt}
              attachment={getTemplateData().attachment}
              handleDialogClose={handleDialogClose}
            />
          )}
        </DialogContent>
      </Dialog>

      {showEditAiTemplateModal && templateForEditModal && (
        <EditTemplateModal
          template={templateForEditModal}
          onClose={handleEditAiTemplateDialogClose}
          onDelete={() => onDeleteTemplate(templateForEditModal._id)}
          templateNameToShow={selectedTemplateName}
        />
      )}
      {/* {showPatientDetailsEditModal && <PatientDetailsEditModal />} */}
      {showPatientDetailsEditModal && (
        <AddPatientPopUp
          open={showPatientDetailsEditModal}
          onOpenChange={setCurrentShowPatientDetailsEditModal}
          onSave={(patient) => {}}
          isDarkMode={darkMode}
          editMode={selectedPatientDetails} // 👈 prefill happens here
        />
      )}

      {showAddVitalsModal && <PatientAddVitalsModal />}
      {showDeleteVitalsModal && <PatientDeleteVitalsModal />}
      {showAddAllergiessModal && <PatientAddAllergiesModal />}
      {showDeleteAllergiesModal && <PatientDeleteAllergiesModal />}
      {showAddConditionRelatedModal && <PatientAddConditionRelatedModal />}
      {showDeleteConditionRelatedModal && (
        <PatientDeleteConditionRelatedModal />
      )}
      {showAddMedicationsModal && <PatientAddMedicationsModal />}
      {showDeleteMedicationsModal && <PatientDeleteMedicationsModal />}
      {showPreviousConsultationsModal && <PatientPreviousConsultationsModal />}
      {previewPrescription && <PrescriptionImagePreviewModal />}
      {showPreConsultImagePreviewModal && <PreConsultImagePreviewModal />}
      {consultationLoadError && (
        <ErrorConsultationModal
          open={consultationLoadError}
          onClose={() => setConsultationLoadError(false)}
        />
      )}

      <ViewDocumentModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        title={viewingDoc?.title}
        documentUrl={viewingDoc?.url}
        mimeType={viewingDoc?.mimeType}
      />

      <MultipleVaccinationPreviewModal />
      <ReferralLetterDialog onLetterReady={handleReferralLetterReady} />
      {referralModalOpen && (
        <ReferralModal
          trigger={<span />}
          initialOpen
          initialTitle={referralModalPayload.title}
          initialSubject={referralModalPayload.subject}
          initialContent={referralModalPayload.content}
          documentType={referralModalPayload.documentType}
          patientName={
            selectedPatientDetails?.patientName ||
            `${selectedPatientDetails?.foreName || ""} ${
              selectedPatientDetails?.surName || ""
            }`.trim()
          }
          patientEmail={
            selectedPatientDetails?.email ||
            selectedPatientDetails?.contacts?.find((c) => !!c.value)?.value ||
            ""
          }
          patientSurgery={
            selectedPatientDetails?.doctor?.surgeryName ||
            selectedPatientDetails?.surgeryName ||
            selectedPatientDetails?.surgery ||
            selectedPatientDetails?.surgery?.name ||
            ""
          }
          onClose={() => setReferralModalOpen(false)}
          onBack={() => {
            setReferralModalOpen(false);
            setShowReferralLetterDialog(true);
          }}
        />
      )}
    </>
  );
};

export default PatientConsultation;
